/**
 * <ry-example>
 *
 * Displays code and live rendered output side-by-side from a single source.
 * Code is shown on the left using ry-code, live preview on the right.
 *
 * Usage:
 * <ry-example title="Accordion">
 *   <template>
 *     <accordion>
 *       <accordion-item title="FAQ" open>
 *         Content here
 *       </accordion-item>
 *     </accordion>
 *   </template>
 * </ry-example>
 */

import { RyElement } from '../core/ry-element.js';
import { transform } from '../core/ry-transform.js';

export class RyExample extends RyElement {
  setup(): void {
    const template = this.$<HTMLTemplateElement>('template');
    if (!template) return;

    // Get clean source (with normalized indentation)
    const source = this.#trimIndent(template.innerHTML);

    // Transform for live rendering
    const transformed = transform(source);

    // Build layout structure
    const stacked = this.hasAttribute('stacked');
    this.innerHTML = `
      <div data-ry-target="preview" class="ry-example__preview"></div>
      <div data-ry-target="code" class="ry-example__code"></div>
    `;

    if (stacked) {
      this.setAttribute('data-stacked', '');
    }

    // Create and inject ry-code with source
    const codeEl = document.createElement('ry-code');
    codeEl.setAttribute('language', 'html');
    codeEl.setAttribute('title', this.getAttribute('title') ?? 'Example');
    codeEl.textContent = source;
    this.$('[data-ry-target="code"]')!.appendChild(codeEl);

    // Create and inject live preview
    const previewTemplate = document.createElement('template');
    previewTemplate.innerHTML = transformed;
    this.$('[data-ry-target="preview"]')!.appendChild(previewTemplate.content);
  }

  /**
   * Normalize indentation by removing common leading whitespace
   */
  #trimIndent(html: string): string {
    const lines = html.split('\n');

    // Remove empty first/last lines
    while (lines.length && !lines[0]!.trim()) lines.shift();
    while (lines.length && !lines[lines.length - 1]!.trim()) lines.pop();

    if (lines.length === 0) return '';

    // Find minimum indentation (ignoring empty lines)
    const minIndent = Math.min(
      ...lines
        .filter(line => line.trim())
        .map(line => {
          const match = line.match(/^(\s*)/);
          return match?.[1]?.length ?? 0;
        })
    );

    // Remove common indentation
    return lines
      .map(line => line.slice(minIndent))
      .join('\n')
      .trim();
  }
}

customElements.define('ry-example', RyExample);
